<template>
    <AiSummaryWidgetWrapper
        :widget-data="props.data"
        :has-content="items.length > 0"
        :call-count="callCount"
        :included-call-count="includedCallCount"
        :skipped-call-count="skippedCallCount"
        :analysis-progress="analysisProgress"
        :is-regenerating="isRegenerating"
        :is-exporting="isExporting"
        :is-downloading-md="isDownloadingMd"
        wrapper-class="summary-renderer py-4 px-2"
        @regenerate="onRegenerateSummary"
        @export-docx="onExportToDocx"
        @download-md="onDownloadMarkdown"
    >
        <AiSummaryList
            variant="bullets"
            :items="items"
        />
    </AiSummaryWidgetWrapper>
</template>

<script setup lang="ts">
//TODO CHECK STYLES AND UI AFTER MIGRATION

import { computed, ref } from 'vue'
import { AISummaryResponse, WidgetExtended } from '@/types/dashboard'
import useNextUpdatedWidgetData from '@/composables/useNextUpdatedWidgetData'
import AiSummaryWidgetWrapper from '@/components/RedesignComponents/Widgets/Common/AiSummaryWidgetWrapper.vue'
import AiSummaryList from '@/components/RedesignComponents/Widgets/Common/AiSummaryList.vue'
import { useAiSummaryProgress } from '@/composables/useAiSummaryProgress'
import { regenerateAiSummary, exportMarkdownToDocx, downloadMarkdownFile } from '@/helpers/aiSummaryHelpers'

/* -------------------------------------
 * Props
 * ----------------------------------- */
const props = defineProps<{
    data: WidgetExtended
}>()

const { data, run } = useNextUpdatedWidgetData<
    AISummaryResponse,
    typeof props,
    'data'
>(props, 'data')

const isRegenerating = ref(false)
const isExporting = ref(false)
const isDownloadingMd = ref(false)

const items = computed<string[]>(() => {
    const raw = data?.value?.SummaryText
    if (!raw) return []

    try {
        const parsed = JSON.parse(data.value.SummaryText)
        return Array.isArray(parsed?.items) ? parsed.items : []
    } catch (e) {
        console.warn('Invalid SummaryText JSON', e)
        return []
    }
})

const markdownContent = computed(() => {
    if (!items.value.length) return ''
    return items.value.map(item => `- ${item}`).join('\n')
})

const {
    callCount,
    includedCallCount,
    skippedCallCount,
    analysisProgress
} = useAiSummaryProgress(data)

/* -------------------------------------
 * Action handlers
 * ----------------------------------- */
async function onRegenerateSummary () {
    if (!props.data.WidgetID || isRegenerating.value) return

    isRegenerating.value = true
    try {
        await regenerateAiSummary(props.data.WidgetID, run)
    } catch (error) {
        console.error('Failed to regenerate summary:', error)
    } finally {
        isRegenerating.value = false
    }
}

async function onExportToDocx () {
    if (!markdownContent.value || isExporting.value) return
    isExporting.value = true
    try {
        await exportMarkdownToDocx(markdownContent.value, props.data)
    } catch (error) {
        console.error('Failed to export to DOCX:', error)
    } finally {
        isExporting.value = false
    }
}

function onDownloadMarkdown () {
    if (!markdownContent.value || isDownloadingMd.value) return

    isDownloadingMd.value = true
    try {
        downloadMarkdownFile(markdownContent.value, props.data)
    } catch (error) {
        console.error('Failed to download markdown:', error)
    } finally {
        isDownloadingMd.value = false
    }
}
</script>

<style scoped>

.vc-markdown-editor.disabled .milkdown {
  color: inherit !important;
  border-width: 0;
}
.vc-markdown-editor__wrapper {
  border-width: 0 !important;
}
</style>
